import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  Row, Col, Button, Input, Tabs, Table, Form, Select,
  DatePicker, Radio, Tooltip, notification, Modal
} from "antd";
import {
  SearchOutlined, DownOutlined, UpOutlined, InfoCircleFilled
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { StyledModal, StyledFormInput, StyledFormSelect, CancelBtn, ActionBtn } from "./AccountModal";
import TransactionViewModal from "./TransactionViewModal";
import TransactionFilterModal from "./TransactionFilterModal";
import { useFetch } from "hooks/useFetch";
import { useMutate } from "hooks/useMutate";
import queryKeys from "common/queryKey";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList, getAccountsList } from "redux/selectors";
import { currencySymbolMap } from "common/Constants";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { kenyaFormatDateTime } from "utils/Date";
import BillingIcon from "assets/billdesicon.svg";

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const fmt = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n % 1 === 0 ? String(Math.trunc(n)) : n.toString();
};

const getCurrencySymbol = (code) => currencySymbolMap[code] ?? code ?? "$";

const MOVEMENT_CONFIG = {
  "Between Accounts": { source: "account", destination: "account" },
  "Between Matters": { source: "matter", destination: "matter" },
  "From Matter to Account": { source: "matter", destination: "account" },
  "From Account to Matter": { source: "account", destination: "matter" },
};
const MOVEMENT_TYPES = Object.keys(MOVEMENT_CONFIG);

const Breadcrumb = styled.div`
  font-size: 15px; color: #888; margin-bottom: 20px;
  span.link    { color: #000; cursor: pointer; &:hover { text-decoration: underline; } }
  span.sep     { margin: 0 6px; }
  span.current { color: #333; font-weight: 500; }
`;
const OutlineBtn = styled(Button)`
  border-radius: 6px; height: 36px;
  border: 1.5px solid #333; color: #333; background: white;
  &:hover, &:focus { border-color: #000 !important; color: #000 !important; }
`;
const TealBtn = styled(Button)`
  border-radius: 6px; height: 36px;
  background-color: #00685F; border-color: #00685F; color: #fff !important;
   &:hover,
  &:focus,
  &:active,
  &.active {
	background-color: #00685F; border-color: #00685F; color: #fff !important;
  }
`;
const SearchInput = styled(Input)`
  width: 140px;
  &.ant-input-affix-wrapper { border-radius: 20px !important; }
  .ant-input-prefix { color: #aaa; }
`;
const FundAllocLink = styled.span`
  color: #0000FF; cursor: pointer; font-size: 13px;
  display: flex; align-items: center; gap: 6px; user-select: none;
  &:hover { text-decoration: underline; }
`;
const SubTableWrapper = styled.div`
  background: #fafafa; border: 1px solid #e8e8e8;
  border-radius: 8px; margin: 8px 16px 16px; overflow: hidden;
`;

const AmountInputGroup = styled.div`
  display: flex; border: 1px solid #d9d9d9;
  border-radius: 6px; overflow: hidden;
  &:hover, &:focus-within {
    border-color: #52c6b0;
    box-shadow: 0 0 0 2px rgba(82,198,176,0.15);
  }
  ${({ $disabled }) =>
    $disabled &&
    `
    background: #f5f5f5;
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
    &:hover, &:focus-within {
      border-color: #d9d9d9;
      box-shadow: none;
    }
  `}
`;

const CurrencyLabel = styled.div`
  display: flex; align-items: center; padding: 0 10px;
  background: #f5f5f5; border-right: 1px solid #d9d9d9;
  font-size: 13px; color: #555; gap: 4px; white-space: nowrap;
`;

const InlineInput = styled(Input)`
  border: none !important; box-shadow: none !important;
  &:focus { box-shadow: none !important; border: none !important; }
`;

const ReadOnlyBox = styled.div`
  display: flex; align-items: center; height: 40px;
  border: 1px solid #d9d9d9; border-radius: 6px;
  background: #fafafa; overflow: hidden;
`;

const ReadOnlyPrefix = styled.div`
  display: flex; align-items: center; height: 100%;
  padding: 0 12px; background: #f5f5f5;
  border-right: 1px solid #d9d9d9; font-size: 13px; color: #555;
`;

const ReadOnlyValue = styled.div`padding: 0 12px; font-size: 14px; color: #333;`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%; border-radius: 6px;
  &:hover, &.ant-picker-focused {
    border-color: #52c6b0 !important;
    box-shadow: 0 0 0 2px rgba(82,198,176,0.15) !important;
  }
`;

const StyledTextArea = styled(TextArea)`
  border-radius: 6px;
  &:hover, &:focus {
    border-color: #52c6b0 !important;
    box-shadow: 0 0 0 2px rgba(82,198,176,0.15) !important;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab { color: #000 !important; }
  .ant-tabs-tab:hover { color: #000 !important; }
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: #000 !important; }
  .ant-tabs-ink-bar { background: #000 !important; }
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex; gap: 24px; flex-wrap: wrap;
  .ant-radio-inner { border-color: #d9d9d9; }
  .ant-radio-checked .ant-radio-inner { border-color: #52c6b0 !important; background-color: #52c6b0 !important; }
  .ant-radio-checked .ant-radio-inner::after { background-color: #fff !important; }
  .ant-radio:hover .ant-radio-inner { border-color: #52c6b0 !important; }
  .ant-radio-checked::after { border-color: #52c6b0 !important; }
`;

const BalanceSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 6px !important; height: 40px !important;
    align-items: center; display: flex !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #52c6b0 !important;
    box-shadow: 0 0 0 2px rgba(82,198,176,0.15) !important;
  }
  .ant-select-dropdown {
    min-width: 320px !important;
  }
`;

const getClientDisplayName = (c) =>
  c?.clienttype === "company"
    ? c.companyregisteredname || c.clientname
    : c.clientname || c.companyregisteredname;

const MoveFundModal = ({ open, onCancel, onMove, submitting, form, clientList, accountList, allMatters = [], currency }) => {
  const [movementType, setMovementType] = useState("Between Accounts");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [sourceId, setSourceId] = useState(null);
  const [availableAmount, setAvailableAmount] = useState(null);
  const [sourceCurrency, setSourceCurrency] = useState(null);
  const [sourceMatterId, setSourceMatterId] = useState(null);
  const [sourceAccountId, setSourceAccountId] = useState(null);
  const [destinationSelected, setDestinationSelected] = useState(false);
  const [sourceAccountType, setSourceAccountType] = useState(null);

  const { getClientAccountsAndMatters } = queryKeys;

  const { data: clientData, isLoading: isLoadingClientData } = useFetch(
    selectedClientId ? `clientMatters_${selectedClientId}_${movementType}` : null,
    selectedClientId
      ? `${getClientAccountsAndMatters.url}?clientid=${selectedClientId}&typeofmovement=${encodeURIComponent(movementType)}`
      : null,
    { enabled: !!selectedClientId }
  );

  const responseData = clientData?.response?.data ?? {};

  const apiAccountMap = new Map(
    (responseData.accounts ?? [])
      .filter((a) => !!a.accountid)
      .map((a) => [
        a.accountid,
        { id: a.accountid, label: a.accountname, balance: parseFloat(a.amount ?? a.balance ?? 0), currency: a.currency ?? null, accounttype: a.accounttype },
      ])
  );

  const getAccountsMap = new Map(
    (accountList ?? [])
      .filter((a) => !!a.accountid)
      .map((a) => [
        a.accountid,
        { id: a.accountid, label: a.accountname, balance: 0, currency: a.currency ?? null, accounttype: a.accounttype },
      ])
  );

  const mergedAccountsForDestination = Array.from(
    new Map([...getAccountsMap, ...apiAccountMap]).values()
  );

  const allAccounts = Array.from(apiAccountMap.values());

  // matterOptions from API response — used for Between Matters / From Matter to Account
  const rawMatters = (responseData.matters ?? []).filter(
    (m) => !!m.matterid && typeof m.mattername === "string" && m.mattername.trim() !== "" &&
      m.matterstatus !== "Draft" && m.matterStatus !== "Draft"
  );
  const seenMatterKeys = new Set();
  const matterOptions = rawMatters.reduce((acc, m, idx) => {
    const balance = parseFloat(m.balance ?? m.amount ?? 0);
    if (isNaN(balance)) return acc;
    const dedupeKey = `${m.matterid}_${m.accountid}_${balance}`;
    if (seenMatterKeys.has(dedupeKey)) return acc;
    seenMatterKeys.add(dedupeKey);
    acc.push({
      id: `${m.matterid}_${idx}`,
      matterid: m.matterid,
      accountid: m.accountid,
      accountname: m.accountname,
      mattername: m.mattername.trim(),
      label: `${m.mattername.trim()} - ${m.accountname.trim()}`,
      currency: m.currency ?? null,
      balance,
    });
    return acc;
  }, []);

  // destinationMatterOptions — used only for "From Account to Matter"
  // Uses allMatters (full list) filtered by selectedClientId.
  // If a matter is allocated to an account (found in API response.matters),
  // show: mattername - accountname - amount
  // If not allocated to any account, expand across all accounts with amount 0.
  const destinationMatterOptions = useMemo(() => {
    if (!selectedClientId) return [];

    // Get the selected client's name to match against matter's clientname field
    const selectedClient = clientList.find((c) => c.bdo_id === selectedClientId);
    const selectedClientName = selectedClient
      ? (selectedClient.clienttype === "company"
        ? selectedClient.companyregisteredname || selectedClient.clientname
        : selectedClient.clientname || selectedClient.companyregisteredname)
      : null;

    // ✅ Filter allMatters by clientid field matching bdo_id,
    // OR fallback match by clientname if clientid format differs
    const clientMatters = allMatters.filter((m) => {
      if (!m.matterid || !m.mattername?.trim()) return false;
      if (m.matterstatus === "Draft" || m.matterStatus === "Draft") return false;
      if (m.clientid === selectedClientId) return true;
      // fallback: match by clientname string
      if (selectedClientName && m.clientname) {
        return m.clientname.trim() === selectedClientName.trim();
      }
      return false;
    });

    const result = [];
    const seenKeys = new Set();

    // Build a quick lookup: matterid+accountid → balance & currency from API
    const allocatedBalanceMap = new Map();
    // Also track accountid -> currency from API (accounts with $0 may be absent from responseData.accounts)
    const accountCurrencyFromMatters = new Map();
    (responseData.matters ?? []).forEach((a) => {
      if (!a.matterid || !a.accountid) return;
      allocatedBalanceMap.set(`${a.matterid}_${a.accountid}`, {
        balance: parseFloat(a.balance ?? a.amount ?? 0),
        currency: a.currency ?? null,
      });
      if (a.currency && !accountCurrencyFromMatters.has(a.accountid)) {
        accountCurrencyFromMatters.set(a.accountid, a.currency);
      }
    });
    // Also pull currency from responseData.accounts
    (responseData.accounts ?? []).forEach((a) => {
      if (a.accountid && a.currency && !accountCurrencyFromMatters.has(a.accountid)) {
        accountCurrencyFromMatters.set(a.accountid, a.currency);
      }
    });

    // For every client matter, expand across ALL accounts (not just allocated ones).
    // Use allocated balance/currency where available, otherwise $0 and account currency.
    clientMatters.forEach((m, idx) => {
      mergedAccountsForDestination.forEach((acct, aIdx) => {
        const key = `${m.matterid}_${acct.id}`;
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        const allocated = allocatedBalanceMap.get(key);
        // Resolve currency: allocated > acct (merged) > API map > matter currency
        const resolvedCurrency = allocated?.currency ?? acct.currency ?? accountCurrencyFromMatters.get(acct.id) ?? m.currency ?? null;
        result.push({
          id: `${m.matterid}_${acct.id}_${aIdx}`,
          matterid: m.matterid,
          accountid: acct.id,
          accountname: acct.label?.trim() ?? "",
          mattername: m.mattername.trim(),
          label: `${m.mattername.trim()} - ${acct.label?.trim() ?? ""}`,
          currency: resolvedCurrency,
          balance: allocated?.balance ?? 0,
        });
      });
    });

    return result;
  }, [selectedClientId, clientList, allMatters, responseData.matters, mergedAccountsForDestination]);

  const isLoading = isLoadingClientData && !!selectedClientId;
  const cfg = MOVEMENT_CONFIG[movementType] || MOVEMENT_CONFIG["Between Accounts"];

  const sourceOptions = (cfg.source === "account" ? allAccounts : matterOptions)
    .filter((o) => o.balance > 0);

  const destinationOptions = (() => {
    if (cfg.destination === "account") {
      const options = mergedAccountsForDestination.filter((o) => {
        if (o.id === sourceId) return false;
        if (!sourceCurrency) return true;
        return (o.currency ?? null) === sourceCurrency;
      });
      // Add External Account when source account is Client Account
      if (sourceAccountType === "Client Account") {
        options.unshift({
          id: "EXTERNAL_ACCOUNT",
          label: "External Account",
          balance: 0,
          currency: sourceCurrency,
          isExternal: true,
        });
      }
      return options;
    }
    if (cfg.source === "account" && cfg.destination === "matter") {
      // From Account to Matter: show all matters when no source selected, else filter by source currency
      return destinationMatterOptions.filter((o) => {
        if (!sourceCurrency) return true;
        return (o.currency ?? null) === sourceCurrency;
      });
    }
    // Between Matters — use destinationMatterOptions (full expanded list including $0 accounts)
    // Exclude all entries belonging to the same source matter (matched via sourceMatterId state)
    if (cfg.source === "matter" && cfg.destination === "matter") {
      return destinationMatterOptions
        .filter((o) => {
          if (sourceMatterId && sourceAccountId) {
            return !(o.matterid === sourceMatterId && o.accountid === sourceAccountId);
          }
          return true;
        })
        .filter((o) => {
          if (!sourceCurrency) return true;
          return (o.currency ?? null) === sourceCurrency;
        });
    }
    // From Matter to Account
    return destinationMatterOptions
      .filter((o) => o.id !== sourceId)
      .filter((o) => {
        const byCurrency = !currency || !o.currency || o.currency === currency;
        if (!sourceCurrency) return byCurrency;
        return byCurrency && (o.currency ?? null) === sourceCurrency;
      });
  })();

  const amountEnabled = !!sourceId && destinationSelected;

  // --- Diagnostics: surfaces exactly why a matter dropdown might be empty ---
  useEffect(() => {
    if (!selectedClientId) return;
    if (cfg.source !== "matter" && cfg.destination !== "matter") return;

    if (cfg.source === "matter" && !isLoading && matterOptions.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        "[MoveFundModal] No matter source options resolved.",
        {
          selectedClientId,
          movementType,
          rawMattersFromAPI: responseData?.matters?.length ?? 0,
          note:
            "Either getClientAccountsAndMatters returned no matters for this client, " +
            "or every returned matter has balance <= 0 (sourceOptions filters those out).",
        }
      );
    }

    if (allMatters.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        "[MoveFundModal] allMatters (from getAllMatersList) is empty. " +
        "Check the Network tab for that request — if it never appears at all, " +
        "verify queryKeys.getAllMatersList is defined and its .url is not undefined/null, " +
        "and that useFetch isn't gating on a falsy url."
      );
    }
  }, [selectedClientId, cfg, isLoading, matterOptions.length, allMatters.length, responseData, movementType]);

  const resetSelectionFields = (extra = {}) => {
    setDestinationSelected(false);
    form.setFieldsValue({
      sourceid: undefined,
      sourceRealId: undefined,
      sourcename: undefined,
      sourceAccountId: undefined,
      sourceAccountName: undefined,
      destinationid: undefined,
      destinationRealId: undefined,
      destinationname: undefined,
      destinationAccountId: undefined,
      destinationAccountName: undefined,
      amountTransferring: undefined,
      ...extra,
    });
  };

  const handleClientChange = (val) => {
    setSelectedClientId(val);
    setSourceId(null);
    setAvailableAmount(null);
    setSourceCurrency(null);
    setSourceMatterId(null);
    setSourceAccountId(null);
    resetSelectionFields({ clientid: val });
  };

  const handleMovementChange = (val) => {
    setMovementType(val);
    setSourceId(null);
    setAvailableAmount(null);
    setSourceCurrency(null);
    setSourceMatterId(null);
    setSourceAccountId(null);
    resetSelectionFields({ typeOfMovement: val });
  };

  const handleSourceChange = (val) => {
    setSourceId(val);
    setDestinationSelected(false);

    const list =
      cfg.source === "account"
        ? Array.from(new Map([...getAccountsMap, ...apiAccountMap]).values())
        : matterOptions;

    const found = list.find((o) => o.id === val);

    setAvailableAmount(found ? found.balance : null);
    setSourceCurrency(found?.currency ?? null);
    setSourceMatterId(found?.matterid ?? null);
    setSourceAccountId(found?.accountid ?? null);

    // Track account type
    setSourceAccountType(found?.accounttype ?? null);

    form.setFieldsValue({
      sourceid: val,
      sourceRealId: found?.matterid ?? found?.id ?? val,
      sourcename:
        cfg.source === "matter"
          ? (found?.mattername ?? found?.label ?? "")
          : (found?.label ?? ""),
      sourceAccountId: found?.accountid ?? null,
      sourceAccountName: found?.accountname ?? null,

      destinationid: undefined,
      destinationRealId: undefined,
      destinationname: undefined,
      destinationAccountId: undefined,
      destinationAccountName: undefined,

      amountTransferring: undefined,
    });
  };

  const handleDestinationChange = (val) => {
    if (val === "EXTERNAL_ACCOUNT") {
      setDestinationSelected(true);

      form.setFieldsValue({
        destinationid: val,
        destinationRealId: val,
        destinationname: "External Account",
        destinationAccountId: null,
        destinationAccountName: "External Account",
      });

      return;
    }

    const list =
      cfg.destination === "account"
        ? mergedAccountsForDestination
        : destinationMatterOptions;

    const found = list.find((o) => o.id === val);

    setDestinationSelected(!!val);

    form.setFieldsValue({
      destinationid: val,
      destinationRealId: found?.matterid ?? found?.id ?? val,
      destinationname:
        cfg.destination === "matter"
          ? (found?.mattername ?? found?.label ?? "")
          : (found?.label ?? ""),
      destinationAccountId: found?.accountid ?? null,
      destinationAccountName: found?.accountname ?? null,
    });
  };

  useEffect(() => {
    if (!open) {
      setMovementType("Between Accounts");
      setSelectedClientId(null);
      setSourceId(null);
      setAvailableAmount(null);
      setSourceCurrency(null);
      setSourceMatterId(null);
      setSourceAccountId(null);
      setDestinationSelected(false);
    }
  }, [open]);

  const sourceDisabled = !selectedClientId || isLoading;
  const destDisabled = !selectedClientId || isLoading;

  const renderOption = (opt) => {
    const optCurrency = opt.currency ?? currency;
    const symbol = getCurrencySymbol(optCurrency);
    return (
      <Option
        key={opt.id}
        value={opt.id}
        label={opt.isExternal ? opt.label : `${opt.label} (${symbol}${fmt(opt.balance)})`}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <span style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            flex: 1,
            lineHeight: "1.4",
          }}>
            {opt.label}
          </span>
          {!opt.isExternal && (
            <span style={{
              color: "#888",
              fontSize: 12,
              whiteSpace: "nowrap",
              flexShrink: 0,
              paddingTop: 2,
            }}>
              ({optCurrency ? `${optCurrency} ` : ""}{symbol}{fmt(opt.balance)})
            </span>
          )}
        </div>
      </Option>
    );
  };

  return (
    <StyledModal
      open={open}
      onCancel={onCancel}
      width={780}
      destroyOnClose
      title="Moving Fund"
      footer={[
        <CancelBtn key="cancel" onClick={onCancel} disabled={submitting}>Cancel</CancelBtn>,
        <ActionBtn key="move" onClick={onMove} loading={submitting}>Move</ActionBtn>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="clientid" label="Move fund for the client"
              rules={[{ required: true, message: "Client is required" }]} style={{ marginBottom: 16 }}>
              <StyledFormSelect placeholder="Select client" allowClear showSearch optionFilterProp="children" onChange={handleClientChange}>
                {clientList.map((c) => (
                  <Option key={c.bdo_id} value={c.bdo_id}>{getClientDisplayName(c)}</Option>
                ))}
              </StyledFormSelect>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dateOfMovement" label="Date of Movement"
              rules={[{ required: true, message: "Date is required" }]} style={{ marginBottom: 16 }}>
              <StyledDatePicker placeholder="Select date" format="Do MMMM YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="typeOfMovement" label="Type of Movement" initialValue="Between Accounts"
              rules={[{ required: true, message: "Please select a movement type" }]} style={{ marginBottom: 20 }}>
              <StyledRadioGroup onChange={(e) => handleMovementChange(e.target.value)}>
                {MOVEMENT_TYPES.map((t) => <Radio key={t} value={t}>{t}</Radio>)}
              </StyledRadioGroup>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="sourceid"
              label={
                !selectedClientId
                  ? <span>Source <span style={{ color: "#aaa", fontSize: 12 }}>(select client first)</span></span>
                  : isLoading
                    ? <span>Source <span style={{ color: "#52c6b0", fontSize: 12 }}>(loading...)</span></span>
                    : "Source"
              }
              rules={[{ required: true, message: "Source is required" }]}
              style={{ marginBottom: 16 }}
            >
              <BalanceSelect
                placeholder={isLoading ? "Loading..." : cfg.source === "account" ? "Select account" : "Select matter"}
                allowClear showSearch
                disabled={sourceDisabled} loading={isLoading}
                optionLabelProp="label" onChange={handleSourceChange}
              >
                {sourceOptions.map(renderOption)}
              </BalanceSelect>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="destinationid"
              label={
                !selectedClientId
                  ? <span>Destination <span style={{ color: "#aaa", fontSize: 12 }}>(select client first)</span></span>
                  : isLoading
                    ? <span>Destination <span style={{ color: "#52c6b0", fontSize: 12 }}>(loading...)</span></span>
                    : "Destination"
              }
              rules={[{ required: true, message: "Destination is required" }]}
              style={{ marginBottom: 16 }}
            >
              <BalanceSelect
                placeholder={isLoading ? "Loading..." : cfg.destination === "account" ? "Select account" : "Select matter"}
                allowClear showSearch
                disabled={destDisabled} loading={isLoading}
                optionLabelProp="label" onChange={handleDestinationChange}
              >
                {destinationOptions.map(renderOption)}
              </BalanceSelect>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Available Amount" style={{ marginBottom: 16 }}>
              <ReadOnlyBox>
                <ReadOnlyPrefix>{getCurrencySymbol(sourceCurrency ?? currency)}{sourceCurrency ? ` ${sourceCurrency}` : ""}</ReadOnlyPrefix>
                <ReadOnlyValue>{availableAmount !== null ? availableAmount : "—"}</ReadOnlyValue>
              </ReadOnlyBox>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="amountTransferring"
              label={
                <span>
                  Amount Transferring{" "}
                  {!amountEnabled && (
                    <span style={{ color: "#aaa", fontSize: 12 }}>
                      (select source & destination first)
                    </span>
                  )}
                </span>
              }
              rules={[
                { required: true, message: "Amount is required" },
                {
                  validator: (_, value) => {
                    if (!value || isNaN(value) || Number(value) <= 0)
                      return Promise.reject("Enter a valid amount");
                    if (availableAmount !== null && Number(value) > Number(availableAmount))
                      return Promise.reject(`Cannot exceed available amount (${getCurrencySymbol(sourceCurrency ?? currency)}${fmt(availableAmount)})`);
                    return Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <AmountInputGroup $disabled={!amountEnabled}>
                <CurrencyLabel>{getCurrencySymbol(sourceCurrency ?? currency)} {sourceCurrency ?? currency}</CurrencyLabel>
                <InlineInput
                  placeholder="0"
                  type="number"
                  min={0}
                  disabled={!amountEnabled}
                />
              </AmountInputGroup>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="description" label="Description" rules={[{ required: true, message: "Description is required" }]} style={{ marginBottom: 16 }}>
              <StyledTextArea rows={4} />
            </Form.Item>
          </Col>

          <Form.Item name="sourcename" hidden><Input /></Form.Item>
          <Form.Item name="sourceRealId" hidden><Input /></Form.Item>
          <Form.Item name="sourceAccountId" hidden><Input /></Form.Item>
          <Form.Item name="sourceAccountName" hidden><Input /></Form.Item>
          <Form.Item name="destinationname" hidden><Input /></Form.Item>
          <Form.Item name="destinationRealId" hidden><Input /></Form.Item>
          <Form.Item name="destinationAccountId" hidden><Input /></Form.Item>
          <Form.Item name="destinationAccountName" hidden><Input /></Form.Item>
        </Row>
      </Form>
    </StyledModal>
  );
};


const AccountDetail = ({ account, onBack, refetchAccounts }) => {
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const accountList = useSelector(getAccountsList);

  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [addFundOpen, setAddFundOpen] = useState(false);
  const [addFundClientId, setAddFundClientId] = useState(null);
  const [moveFundOpen, setMoveFundOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isDescModalOpen, setIsDescModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filterForm] = Form.useForm();

  const [txFilters, setTxFilters] = useState({
    createdby: [],
    clientid: [],
    matterid: [],
    category: [],
    sourcename: [],
    destinationname: [],
    fundin: "",
    fundout: "",
    fundbalance: "",
    datefrom: null,
    dateto: null,
  });

  const [txPage, setTxPage] = useState(1);
  const TX_PAGE_SIZE = 10;
  const [txViewRecord, setTxViewRecord] = useState(null);

  const [pendingBillingRecord, setPendingBillingRecord] = useState(null);

  const [addForm] = Form.useForm();
  const [moveForm] = Form.useForm();

  const {
    addFund, moveFund, getFunds,
    getTransactionHistory, getTransactionFilterList, getAllClientList, getAllMatersList,
    getBillingById,
  } = queryKeys;

  console.log("getAllMatersList raw:", getAllMatersList);

  // --- Diagnostics: confirms whether getAllMatersList is even configured ---
  useEffect(() => {
    if (!getAllMatersList) {
      // eslint-disable-next-line no-console
      console.error(
        "[AccountDetail] queryKeys.getAllMatersList is undefined. " +
        "Check common/queryKey.js for a typo in the key name (e.g. 'getAllMattersList' vs 'getAllMatersList')."
      );
    } else if (!getAllMatersList.url) {
      // eslint-disable-next-line no-console
      console.error(
        "[AccountDetail] queryKeys.getAllMatersList.url is falsy:",
        getAllMatersList,
        "— useFetch likely won't fire a request. Check the base URL / env var used to build it."
      );
    }
  }, [getAllMatersList]);

  const { data: clientData, isLoading: isLoadingClients } = useFetch(getAllClientList.key, getAllClientList.url);
  const CLIENT_DATA = clientData?.response?.data ?? [];

  const { data: matterData, error: matterError, isLoading: isLoadingMatters } = useFetch(
    userInfo?.userid ? getAllMatersList(userInfo.userid).key : null,
    userInfo?.userid ? getAllMatersList(userInfo.userid).url : null
  );
  const MATTER_DATA = matterData?.response?.data ?? [];

  useEffect(() => {
    if (matterError) {
      // eslint-disable-next-line no-console
      console.error("[AccountDetail] getAllMatersList request failed:", matterError);
    }
  }, [matterError]);

  useEffect(() => {
    if (!isLoadingMatters && getAllMatersList?.url && MATTER_DATA.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        "[AccountDetail] getAllMatersList returned 0 matters.",
        "If you expected data here, check the Network tab for a request to:",
        getAllMatersList.url
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMatters, MATTER_DATA.length]);

  const addFundMatters = useMemo(() => {
    if (!addFundClientId) return [];

    const selectedClient = CLIENT_DATA.find((c) => c.bdo_id === addFundClientId);
    const selectedClientName = selectedClient ? getClientDisplayName(selectedClient) : null;

    return MATTER_DATA.filter((m) => {
      if (!m.matterid || !m.mattername?.trim()) return false;
      if (m.matterstatus === "Draft" || m.matterStatus === "Draft") return false;

      // Match client by id first, falling back to name — API clientid formats
      // have been observed to differ from bdo_id (see destinationMatterOptions
      // in MoveFundModal, which already needed this same fallback).
      const clientMatches =
        String(m.clientid ?? "") === String(addFundClientId ?? "") ||
        (!!selectedClientName && !!m.clientname && m.clientname.trim().toLowerCase() === selectedClientName.trim().toLowerCase());
      if (!clientMatches) return false;

      // Currency match — lenient: if either side is missing currency info,
      // don't exclude the matter just because of that.
      const matterCurrency = (m.currency ?? "").toString().trim().toUpperCase();
      const acctCurrency = (account?.currency ?? "").toString().trim().toUpperCase();
      if (matterCurrency && acctCurrency && matterCurrency !== acctCurrency) return false;

      return true;
    });
  }, [addFundClientId, MATTER_DATA, CLIENT_DATA, account?.currency]);

  useEffect(() => {
    if (addFundClientId && MATTER_DATA.length > 0 && addFundMatters.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        "[AccountDetail] addFundMatters resolved to 0 for client",
        addFundClientId,
        "even though MATTER_DATA has",
        MATTER_DATA.length,
        "entries. Likely a clientid/clientname/currency mismatch — inspect a sample matter:",
        MATTER_DATA[0]
      );
    }
  }, [addFundClientId, MATTER_DATA, addFundMatters.length]);

  const txQueryParams = useMemo(() => {
    const p = new URLSearchParams({ accountid: account?.accountid, page: txPage, pagesize: TX_PAGE_SIZE });

    if (txFilters.createdby?.length)
      txFilters.createdby.forEach((v) => p.append("createdby", v));
    if (txFilters.clientid?.length)
      txFilters.clientid.forEach((v) => p.append("clientid", v));
    if (txFilters.matterid?.length)
      txFilters.matterid.forEach((v) => p.append("matterid", v));
    if (txFilters.category?.length)
      txFilters.category.forEach((v) => p.append("category", v));
    if (txFilters.sourcename?.length)
      txFilters.sourcename.forEach((v) => p.append("sourcename", v));
    if (txFilters.destinationname?.length)
      txFilters.destinationname.forEach((v) => p.append("destinationname", v));

    if (txFilters.fundin !== "") p.set("fundin", txFilters.fundin);
    if (txFilters.fundout !== "") p.set("fundout", txFilters.fundout);
    if (txFilters.fundbalance !== "") p.set("fundbalance", txFilters.fundbalance);
    if (txFilters.datefrom) p.set("datefrom", txFilters.datefrom);
    if (txFilters.dateto) p.set("dateto", txFilters.dateto);

    return p.toString();
  }, [account?.accountid, txFilters, txPage]);

  const { data: txData, isLoading: isLoadingTx, refetch: refetchTx } = useFetch(
    `${getTransactionHistory.key}_${account?.accountid}_${txQueryParams}`,
    account?.accountid ? `${getTransactionHistory.url}?${txQueryParams}` : null
  );

  const { data: txFilterListData, refetch: refetchTxFilterList } = useFetch(
    account?.accountid ? `${getTransactionFilterList.key}_${account?.accountid}` : null,
    account?.accountid ? `${getTransactionFilterList.url}?accountid=${account?.accountid}` : null
  );

  const billingId = pendingBillingRecord?.billingid;
  const clientId = pendingBillingRecord?.clientid;
  const billingQueryParams = billingId && clientId
    ? `billingid=${billingId}&clientid=${clientId}`
    : null;
  const { data: billingApiData, isLoading: isLoadingBilling } = useFetch(
    billingQueryParams ? getBillingById(billingQueryParams).key : null,
    billingQueryParams ? getBillingById(billingQueryParams).url : null
  );

  useEffect(() => {
    if (!pendingBillingRecord || isLoadingBilling) return;
    const fetched = billingApiData?.response ?? null;
    setTxViewRecord({
      ...pendingBillingRecord,
      billingdata: fetched ? fetched?.data?.[0] : {},
      clientRes: fetched ? fetched?.clientRes : {},
    });
    setPendingBillingRecord(null);
  }, [billingApiData, isLoadingBilling]);

  const {
    data: allFundsData,
    isLoading: isLoadingAllFunds,
    refetch: refetchAllFunds,
  } = useFetch(
    `${getFunds.key}_all_${account?.accountid}`,
    account?.accountid ? `${getFunds.url}?accountid=${account?.accountid}` : null
  );

  const allFundsList = useMemo(() => allFundsData?.response?.data ?? [], [allFundsData]);

  const clientAllocMap = useMemo(() => {
    const map = {};
    allFundsList.forEach((item) => {
      if (!item.clientid) return;
      if (!map[item.clientid]) map[item.clientid] = [];
      map[item.clientid].push(item);
    });
    return map;
  }, [allFundsList]);
  console.log("addFundClientId:", addFundClientId, "MATTER_DATA:", MATTER_DATA.length, "addFundMatters:", addFundMatters.length, MATTER_DATA[0]);

  const { mutate: addFundMutate } = useMutate(addFund.key, addFund.url);
  const { mutate: moveFundMutate } = useMutate(moveFund.key, moveFund.url);

  const txList = txData?.response?.data ?? [];
  const txPagination = txData?.response?.pagination ?? {};
  const txFilterList = txFilterListData?.response?.data ?? {};

  const txDisplayList = txList;

  const filterActionByUsers = txFilterList.createdby ?? [];
  const filterClients = txFilterList.clients ?? [];
  const filterMatters = txFilterList.matters ?? [];
  const filterCategories = txFilterList.categories ?? [];
  const filterSources = txFilterList.sources ?? [];
  const filterDestinations = txFilterList.destinations ?? [];

  const setTxFilter = (key, val) => {
    setTxPage(1);
    setTxFilters((prev) => ({ ...prev, [key]: val ?? [] }));
  };

  const getClientBalance = (c) => {
    const raw = clientAllocMap[c.bdo_id];
    const allocs = Array.isArray(raw) ? raw : [];
    const fetched = c.bdo_id in clientAllocMap;
    const total = allocs.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    return fetched && allocs.length ? total : (c.totalbalance ?? 0);
  };

  const filteredClients = CLIENT_DATA.filter((c) =>
    !searchText || (getClientDisplayName(c) || "").toLowerCase().includes(searchText.toLowerCase())
  );

  const allBalancesZero = filteredClients.every((c) => getClientBalance(c) === 0);
  const clientTableData = allBalancesZero
    ? []
    : filteredClients.filter((c) => getClientBalance(c) > 0);

  const toggleExpand = (clientid) => {
    setExpandedKeys((prev) => prev.includes(clientid) ? [] : [clientid]);
  };

  const handleAddFund = async () => {
    try {
      const values = await addForm.validateFields();
      setSubmitting(true);
      const client = CLIENT_DATA.find((c) => c.bdo_id === values.clientid);
      const matter = values.matterid ? MATTER_DATA.find((m) => m.matterid === values.matterid) : null;
      addFundMutate({
        accountid: account?.accountid,
        clientid: values.clientid,
        clientname: getClientDisplayName(client) || "",
        matterid: values.matterid || "MID0",
        mattername: matter ? matter.mattername : "Unallocated Fund",
        dateoftransaction: values.dateOfTransaction?.toISOString() || null,
        amount: parseFloat(values.amountValue),
        currency: account?.currency || "USD",
        source: values.source || null,
        destination: values.destination || null,
        paymentmethod: values.paymentMethod || null,
        otherremarks: values.otherRemarks || null,
        description: values.description || null,
        createdby: userInfo?.userid || null,
      }, {
        onSuccess: (res) => {
          notification.success({ message: res?.response?.message || "Fund added successfully!" });
          refetchTx();
          refetchAllFunds();
          refetchTxFilterList();
          refetchAccounts?.();
          addForm.resetFields();
          setAddFundOpen(false);
        },
        onError: (err) => notification.error({ message: err?.message || "Failed to add fund." }),
        onSettled: () => setSubmitting(false),
      });
    } catch (_) { setSubmitting(false); }
  };

  const handleMoveFund = async () => {
    try {
      const values = await moveForm.validateFields();
      setSubmitting(true);

      const client = CLIENT_DATA.find((c) => c.bdo_id === values.clientid);
      const cfg = MOVEMENT_CONFIG[values.typeOfMovement];
      const destinationId = values.destinationid === "EXTERNAL_ACCOUNT" ? 0 : (values.destinationRealId || values.destinationid);
      const payload = {
        accountid: account?.accountid,
        clientid: values.clientid,
        clientname: getClientDisplayName(client) || "",
        typeofmovement: values.typeOfMovement,
        sourcetype: cfg.source,
        sourceid: values.sourceRealId || values.sourceid,
        sourcename: values.sourcename || "",
        destinationtype: cfg.destination,
        destinationid: destinationId,
        destinationname: values.destinationname || "",
        amounttransferring: parseFloat(values.amountTransferring),
        currency: account?.currency || "USD",
        dateofmovement: values.dateOfMovement?.toISOString() || null,
        description: values.description || null,
        createdby: userInfo?.userid || null,
      };

      if (values.typeOfMovement === "Between Matters") {
        payload.sourceaccountid = values.sourceAccountId;
        payload.sourceaccountname = values.sourceAccountName;
        payload.destinationaccountid = values.destinationAccountId;
        payload.destinationaccountname = values.destinationAccountName;
      } else if (values.typeOfMovement === "From Matter to Account") {
        payload.sourceaccountid = values.sourceAccountId;
        payload.sourceaccountname = values.sourceAccountName;
      } else if (values.typeOfMovement === "From Account to Matter") {
        payload.destinationaccountid = values.destinationAccountId;
        payload.destinationaccountname = values.destinationAccountName;
      }

      moveFundMutate(payload, {
        onSuccess: (res) => {
          notification.success({ message: res?.response?.message || "Fund moved successfully!" });
          refetchTx();
          refetchAllFunds();
          refetchTxFilterList();
          refetchAccounts?.();
          setMoveFundOpen(false);
          moveForm.resetFields();
        },
        onError: (err) => notification.error({ message: err?.message || "Failed to move fund." }),
        onSettled: () => setSubmitting(false),
      });
    } catch (_) { setSubmitting(false); }
  };

  const allocationColumns = [
    { title: "Allocation", dataIndex: "mattername", key: "mattername", width: "30%" },
    { title: "Matter ID", dataIndex: "matterid", key: "matterid", width: "20%", render: (v) => (v && v !== "MID0" ? v : "---") },
    { title: "Amount", dataIndex: "amount", key: "amount", width: "20%", render: (v) => v != null ? `${getCurrencySymbol(account?.currency)}${fmt(v)}` : "---" },
  ];

  const clientColumns = [
    {
      title: "Client", key: "clientname", width: "50%",
      render: (_, record) => {
        const displayName = record.clienttype === "company"
          ? record.companyregisteredname || record.clientname
          : record.clientname || record.companyregisteredname;
        return <span style={{ color: "#000", fontWeight: 500 }}>{displayName || "—"}</span>;
      },
    },
    {
      title: "Balance", key: "totalbalance", width: "25%",
      render: (_, record) => {
        const raw = clientAllocMap[record.bdo_id];
        const allocs = Array.isArray(raw) ? raw : [];
        const fetched = record.bdo_id in clientAllocMap;
        const total = allocs.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        const display = fetched && allocs.length ? total : (record.totalbalance ?? 0);
        return `${getCurrencySymbol(account?.currency)} ${fmt(display)}`;
      },
    },
    {
      title: "", key: "expand", width: "25%",
      render: (_, record) => {
        const open = expandedKeys.includes(record.bdo_id);
        return (
          <FundAllocLink onClick={(e) => { e.stopPropagation(); toggleExpand(record.bdo_id); }}>
            Fund Allocation {open ? <UpOutlined /> : <DownOutlined />}
          </FundAllocLink>
        );
      },
    },
  ];

  const txColumns = [
    {
      title: "Action by",
      dataIndex: "createdby",
      key: "createdby",
      width: "20%",
      render: (text, record) => {
        const finduser = userList?.find((item) => item?.userid === text);
        return (
          <span style={{ whiteSpace: "pre-line" }}>
            {finduser?.fullname ? `${finduser.fullname};\n` : ""}
            {kenyaFormatDateTime(record.createdat)}
          </span>
        );
      },
    },
    { title: "Client", dataIndex: "clientname", key: "clientname", width: "14%" },
    {
      title: "Matter", dataIndex: "mattername", key: "mattername", width: "14%",
      render: (text, record) => (record?.matterid !== "MID0" ? (text || "---") : "---"),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "25%",
      render: (t, r) => {
        if (!t) return "---";

        const isBilling = t === "Billing";
        const isFundRequest = t === "Fund Request";

        const label = isBilling
          ? `${t} (${r.billingid})`
          : isFundRequest
            ? `${t} (${r.fundrequestid})`
            : t;

        const isPendingThis = isLoadingBilling && pendingBillingRecord?.billingid === r.billingid;

        return (
          <span
            style={{
              cursor: isPendingThis ? "wait" : "pointer",
              color: "blue",
              textDecoration: "underline",
              opacity: isPendingThis ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={() => {
              const cat = (r.category || "").toLowerCase();
              const isBillingCat = cat.includes("billing") || (r.billingid && r.billingid !== "BID00000");

              if (isBillingCat) {
                setPendingBillingRecord(r);
              } else {
                setTxViewRecord(r);
              }
            }}
          >
            {label}

            <img
              src={BillingIcon}
              alt="desc"
              style={{
                width: 16,
                height: 16,
                cursor: "pointer",
                marginLeft: 6,
              }}
              onMouseEnter={(e) => {
                e.stopPropagation();
                setSelectedRecord(r);
                setIsDescModalOpen(true);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRecord(r);
                setIsDescModalOpen(true);
              }}
            />
          </span>
        );
      },
    },
    { title: "Source", dataIndex: "sourcename", key: "sourcename", width: "12%", render: (v) => v || "---" },
    { title: "Destination", dataIndex: "destinationname", key: "destinationname", width: "12%", render: (v) => v || "---" },
    { title: "Fund Out", dataIndex: "fundout", key: "fundout", width: "8%", render: (v) => v ? `${getCurrencySymbol(account?.currency)}${fmt(v)}` : "---" },
    { title: "Fund In", dataIndex: "fundin", key: "fundin", width: "8%", render: (v) => v ? `${getCurrencySymbol(account?.currency)}${fmt(v)}` : "---" },
    { title: "Fund Balance", dataIndex: "fundbalance", key: "fundbalance", width: "10%", render: (v) => v != null ? `${getCurrencySymbol(account?.currency)}${fmt(v)}` : "---" },
  ];


  return (
    <>
      <Breadcrumb>
        <span className="link" onClick={onBack}>Account</span>
        <span className="sep">›</span>
        <span className="current">{account?.accountname} ({account?.accounttype})</span>
      </Breadcrumb>

      <StyledTabs defaultActiveKey="clients">
        <TabPane tab="Client List" key="clients">
          <Row align="middle" gutter={[12, 8]} style={{ marginBottom: 16 }}>
            <Col flex="none">
              <span style={{ fontSize: 14, color: "#555" }}>Total Clients: {clientTableData.length}</span>
            </Col>
            <Col flex="auto" />
            <Col flex="none">
              <SearchInput placeholder="Search" prefix={<SearchOutlined />}
                value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
            </Col>
            <Col flex="none">
              <OutlineBtn onClick={() => { moveForm.resetFields(); moveForm.setFieldsValue({ dateOfMovement: dayjs() }); setMoveFundOpen(true); }}>Move Fund</OutlineBtn>
            </Col>
            <Col flex="none">
              <TealBtn onClick={() => { addForm.resetFields(); addForm.setFieldsValue({ dateOfTransaction: dayjs() }); setAddFundClientId(null); setAddFundOpen(true); }}>Add Fund</TealBtn>
            </Col>
          </Row>

          <Table
            dataSource={clientTableData} columns={clientColumns}
            pagination={false} rowKey="bdo_id"
            loading={isLoadingClients || isLoadingAllFunds}
            expandable={{
              expandedRowRender: (r) => {
                const allocations = clientAllocMap[r.bdo_id] ?? [];
                return (
                  <SubTableWrapper>
                    <Table dataSource={allocations} columns={allocationColumns}
                      pagination={false} rowKey={(row) => row.matterid || "unallocated"}
                      size="small" loading={isLoadingAllFunds} />
                  </SubTableWrapper>
                );
              },
              expandedRowKeys: expandedKeys,
              showExpandColumn: false,
              expandRowByClick: false,
            }}
          />
        </TabPane>

        <TabPane tab="Transaction History" key="history">
          <Row align="middle" gutter={[12, 8]} style={{ marginBottom: 16 }}>
            <Col flex="none">
              <span style={{ fontSize: 14, color: "#555", fontWeight: 500 }}>Total log: {txPagination.total ?? 0}</span>
            </Col>
            <Col flex="auto" />

            <Form form={filterForm} style={{ marginTop: 20 }} onValuesChange={(_, allValues) => {
              setTxFilter("createdby", allValues.createdby ?? []);
              setTxFilter("clientid", allValues.clientid ?? []);
              setTxFilter("matterid", allValues.matterid ?? []);
              setTxFilter("category", allValues.category ?? []);
            }}>

              <Col flex="none">
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>

                  <div style={{ width: 190 }}>
                    <DropdownCheckBox
                      name="createdby"
                      label="Action by"
                      form={filterForm}
                      options={filterActionByUsers
                        ?.map((u) => {
                          const found = userList?.find((ul) => ul.userid === u.userid);
                          return found
                            ? { label: found.fullname, value: found.userid }
                            : null;
                        })
                        .filter(Boolean)}
                      handleFormChange={() => {
                        const val = filterForm.getFieldValue("createdby") ?? [];
                        setTxFilter("createdby", val);
                      }}
                    />
                  </div>

                  <div style={{ width: 190 }}>
                    <DropdownCheckBox
                      name="clientid"
                      label="Client"
                      form={filterForm}
                      options={filterClients.map((c) => ({
                        label: c.clientname,
                        value: c.clientid,
                      }))}
                      handleFormChange={() => {
                        const val = filterForm.getFieldValue("clientid") ?? [];
                        setTxFilter("clientid", val);
                      }}
                    />
                  </div>

                  <div style={{ width: 190 }}>
                    <DropdownCheckBox
                      name="matterid"
                      label="Matter"
                      form={filterForm}
                      options={filterMatters.map((m) => ({
                        label: m.mattername,
                        value: m.matterid,
                      }))}
                      handleFormChange={() => {
                        const val = filterForm.getFieldValue("matterid") ?? [];
                        setTxFilter("matterid", val);
                      }}
                    />
                  </div>

                  <div style={{ width: 190 }}>
                    <DropdownCheckBox
                      name="category"
                      label="Category"
                      form={filterForm}
                      options={filterCategories.map((cat) => ({
                        label: cat,
                        value: cat,
                      }))}
                      handleFormChange={() => {
                        const val = filterForm.getFieldValue("category") ?? [];
                        setTxFilter("category", val);
                      }}
                    />
                  </div>
                </div>
              </Col>

            </Form>

            <Col flex="none">
              <TransactionFilterModal
                sourceOptions={filterSources}
                destinationOptions={filterDestinations}
                initialValues={{
                  source: txFilters.sourcename,
                  destination: txFilters.destinationname,
                  fundIn: txFilters.fundin,
                  fundOut: txFilters.fundout,
                  fundBalance: txFilters.fundbalance,
                  dateFrom: txFilters.datefrom,
                  dateTo: txFilters.dateto,
                }}
                onApply={(f) => {
                  setTxPage(1);
                  setTxFilters((prev) => ({
                    ...prev,
                    sourcename: f.source ?? [],
                    destinationname: f.destination ?? [],
                    fundin: f.fundIn ?? "",
                    fundout: f.fundOut ?? "",
                    fundbalance: f.fundBalance ?? "",
                    datefrom: f.dateFrom || null,
                    dateto: f.dateTo || null,
                  }));
                }}
                onClear={() => {
                  setTxPage(1);
                  setTxFilters((prev) => ({
                    ...prev,
                    sourcename: [],
                    destinationname: [],
                    fundin: "",
                    fundout: "",
                    fundbalance: "",
                    datefrom: null,
                    dateto: null,
                  }));
                }}
              />
            </Col>
          </Row>

          <Table
            dataSource={txDisplayList}
            columns={txColumns}
            rowKey={(r) => r.transactionid || r.key}
            loading={isLoadingTx}
            scroll={{ x: 900 }}
            pagination={{
              current: txPage,
              pageSize: TX_PAGE_SIZE,
              total: txPagination.total ?? 0,
              onChange: (page) => setTxPage(page),
              showSizeChanger: false,
              showTotal: (total) => `${total} records`,
            }}
          />
        </TabPane>
      </StyledTabs>

      <StyledModal open={addFundOpen} onCancel={() => { setAddFundOpen(false); setAddFundClientId(null); }}
        width={780} destroyOnClose title="Adding Fund"
        footer={[
          <CancelBtn key="cancel" onClick={() => { setAddFundOpen(false); setAddFundClientId(null); }} disabled={submitting}>Cancel</CancelBtn>,
          <ActionBtn key="add" onClick={handleAddFund} loading={submitting}>Add</ActionBtn>,
        ]}
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="clientid" label="Client"
                rules={[{ required: true, message: "Client is required" }]} style={{ marginBottom: 16 }}>
                <StyledFormSelect
                  placeholder="Select client"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    setAddFundClientId(val);
                    addForm.setFieldsValue({ matterid: undefined });
                  }}
                >
                  {CLIENT_DATA.map((c) => (
                    <Option key={c.bdo_id} value={c.bdo_id}>{getClientDisplayName(c)}</Option>
                  ))}
                </StyledFormSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="matterid"
                label={
                  <span>Matter{" "}
                    <Tooltip
                      title="Choose a matter if the funds should be allocated to a specific matter. Leave this field empty to retain the funds at the client level."
                      overlayInnerStyle={{
                        backgroundColor: "#f0f0f0",
                        color: "#333",
                        borderRadius: 8,
                        fontSize: 13,
                        padding: "10px 14px",
                        maxWidth: 300,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      }}
                      overlayStyle={{ maxWidth: 320 }}
                      color="#f0f0f0"
                    >
                      <InfoCircleFilled style={{ color: "#0066FF", marginLeft: 4, cursor: "pointer", fontSize: 16 }} />
                    </Tooltip>
                    {!addFundClientId && (
                      <span style={{ color: "#aaa", fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                        (select client first)
                      </span>
                    )}
                  </span>
                }
                style={{ marginBottom: 16 }}
              >
                <StyledFormSelect
                  placeholder={
                    !addFundClientId
                      ? "Select a client first"
                      : isLoadingMatters
                        ? "Loading matters..."
                        : addFundMatters.length === 0
                          ? "No matters found for this client"
                          : "Select matter (optional)"
                  }
                  allowClear
                  disabled={!addFundClientId}
                  loading={isLoadingMatters}
                  showSearch
                  optionFilterProp="children"
                >
                  {addFundMatters.map((m) => (
                    <Option key={m.matterid} value={m.matterid}>{m.mattername}</Option>
                  ))}
                </StyledFormSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateOfTransaction" label="Date of Transaction"
                rules={[{ required: true, message: "Date is required" }]} style={{ marginBottom: 16 }}>
                <StyledDatePicker placeholder="Select date" format="Do MMMM YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="Amount"
                rules={[{ required: true, message: "Amount is required" }]} style={{ marginBottom: 16 }}>
                <AmountInputGroup>
                  <CurrencyLabel>{getCurrencySymbol(account?.currency)} {account?.currency}</CurrencyLabel>
                  <Form.Item name="amountValue" noStyle>
                    <InlineInput placeholder="200" type="number" min={0} />
                  </Form.Item>
                </AmountInputGroup>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="source" label="Source" style={{ marginBottom: 16 }}><StyledFormInput /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="destination" label="Destination" style={{ marginBottom: 16 }}><StyledFormInput /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMethod" label="Payment method" style={{ marginBottom: 16 }}><StyledFormInput /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="otherRemarks" label="Other Remarks" style={{ marginBottom: 16 }}><StyledFormInput /></Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description" rules={[{ required: true, message: "Description is required" }]} style={{ marginBottom: 16 }}>
                <StyledTextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </StyledModal>

      <MoveFundModal
        open={moveFundOpen}
        onCancel={() => { setMoveFundOpen(false); moveForm.resetFields(); }}
        onMove={handleMoveFund}
        submitting={submitting}
        form={moveForm}
        clientList={CLIENT_DATA}
        accountList={accountList}
        allMatters={MATTER_DATA}
        currency={account?.currency}
      />

      <TransactionViewModal
        open={!!txViewRecord}
        onClose={() => setTxViewRecord(null)}
        record={txViewRecord}
      />

      <Modal
        title="Description"
        open={isDescModalOpen}
        onCancel={() => setIsDescModalOpen(false)}
        footer={null}
      >
        <p>{selectedRecord?.description || "No description available"}</p>
      </Modal>

    </>
  );
};

export default AccountDetail;